import { actions, getters, mutations, state } from "@/store/siteStore";
import { clear, clearListCache, createKey, get } from "@/utils/networkCache";
import { getPaginationParams, updateItem } from "@/utils/storeHelper";
import { request } from "leatherman-js";
import Status from "@/classes/Status";

const CACHE_PLURAL_KEY = "sites";
const CACHE_SINGULAR_KEY = "site";

const store = {
  namespaced: true,
  state,
  mutations,
  actions: {
    ...actions,
    create(context, { domain, installApp, planId }) {
      return request.makeParse("createSite", {}, { domain, installApp, planId });
    },
    createFromStencil(context, { planId, stencilId }) {
      return request.makeParse("createSiteFromStencil", {}, { planId, stencilId });
    },
    createSecondary(context, { id, data }) {
      return request.makeParse("createSecondarySite", { id }, data);
    },
    async get({ commit, state }, id) {
      const newStatus =
        (state.itemStatus.isLoaded || state.itemStatus.isUpdating) && id === state.item?.id
          ? Status.UPDATING
          : Status.LOADING;
      commit("SET_ITEM_STATUS", newStatus);
      try {
        const response = await get(createKey(CACHE_SINGULAR_KEY, { id }), request.makeParse, [
          "getSite",
          { id },
        ]);
        commit("SET_ITEM", response);
        commit("SET_ITEM_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_ITEM_STATUS", Status.ERROR);
      }
    },
    async getList({ commit, state }, params) {
      const newStatus =
        (state.status.isLoaded || state.status.isUpdating) && params?.planId === state.list?.planId
          ? Status.UPDATING
          : Status.LOADING;
      commit("SET_STATUS", newStatus);
      commit("SET_CHUNK", params);
      commit("SET_SORT", params);

      params = { ...getPaginationParams(state), ...params };
      try {
        const response = await get(createKey(CACHE_PLURAL_KEY, params), request.makeParse, [
          "getSitesByPlanId",
          params,
        ]);
        commit("SET_LIST", { list: response.list, planId: response.planId });
        commit("SET_PAGINATION", response.pagination);
        commit("SET_STATUS", Status.LOADED);
        return response;
      } catch (e) {
        commit("SET_STATUS", Status.ERROR);
      }
    },
    update({ commit, dispatch, state }, site) {
      clear(createKey(CACHE_SINGULAR_KEY, { id: site.id }));
      clearListCache(CACHE_PLURAL_KEY, { planId: site.plan.id });
      updateItem(commit, state, site);
      if (!state.status.isInitialized) {
        return;
      }
      dispatch("getList", { planId: site.plan.id });
    },
    async delete({ dispatch }, { id, planId }) {
      const response = await request.makeParse("deleteSite", { id });
      dispatch("refreshList", { planId });
      return response;
    },

    refreshItem({ dispatch, state }, id) {
      const cacheKey = createKey(CACHE_SINGULAR_KEY, { id });
      clear(cacheKey);
      if (id === state.item?.id) {
        dispatch("get", id);
      }
    },
    refreshList({ dispatch, state }, { planId }) {
      clearListCache(CACHE_PLURAL_KEY, { planId });
      if (planId === state.list?.planId) {
        dispatch("getList", { planId });
      }
    },
    refreshListBySiteId({ dispatch, state }, siteId) {
      const site = state.list?.list?.find((item) => item.id === siteId);
      if (site) {
        const planId = state.list?.planId;
        clearListCache(CACHE_PLURAL_KEY, { planId });
        dispatch("getList", {
          planId,
          pageIndex: state.chunk?.pagination?.index,
          pageSize: state.chunk?.pagination?.size,
          sortProperty: state.chunk?.sort?.property,
          sortDirection: state.chunk?.sort?.direction,
        });
      }
    },
  },
  getters,
};

export default store;
